import { Link } from "react-router";
import { ExternalLink, FileText, Hash, KeyRound, LayoutGrid, ListChecks, Plus, Trash2, Workflow } from "lucide-react";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Field, InlineAlert, Input, NativeSelect, Skeleton, Switch, Textarea } from "@ncp/ui";
import { DefinitionEditorPage, type EditorContext } from "../features/generic/DefinitionEditor";
import { fieldKeyFromLabel } from "../features/generic/api";
import { RefPicker, type RefOption } from "../features/workspaces/RefPicker";
import { SERVICE_ICONS, useNumberPreview, useServiceOptions, type OptionRef, type RequiredDocument, type ServiceBody } from "../features/services/api";

type Ctx = EditorContext<ServiceBody>;
type T = (k: string, o?: Record<string, unknown>) => string;

function lint(b: ServiceBody, t: T) {
  const errors: string[] = []; const warnings: string[] = [];
  if (!/^[A-Z0-9]{2,6}$/.test(b.code)) errors.push(t("admin:serviceDesigner.lint.code"));
  if (!b.caseNumberPattern.includes("{seq")) errors.push(t("admin:serviceDesigner.lint.pattern"));
  if (!b.workflowKey) warnings.push(t("admin:serviceDesigner.lint.workflow"));
  if (b.moduleKind === "Dynamic" && b.audience !== "Internal" && !b.intakeFormKey) warnings.push(t("admin:serviceDesigner.lint.form"));
  const keys = b.requiredDocuments.map((d) => d.key);
  for (const d of b.requiredDocuments) if (!d.key || !d.nameEn) errors.push(t("admin:serviceDesigner.lint.document"));
  if (new Set(keys).size !== keys.length) errors.push(t("admin:serviceDesigner.lint.documentTwice"));
  return { errors: [...new Set(errors)], warnings };
}

/** Service designer: what the process is called and who sees it, the workflow and form it runs, its case numbers, documents, rules and roles. */
export function ServiceDesignerPage() {
  const { t } = useTranslation();
  return (
    <DefinitionEditorPage<ServiceBody>
      resource="services"
      basePath="/services"
      navLabel={t("admin:nav.services")}
      icon={LayoutGrid}
      writePermission="platform.services.write"
      docsAnchor="services"
      help={<><p>{t("admin:serviceDesigner.help.what")}</p><p>{t("admin:serviceDesigner.help.dynamic")}</p><p>{t("admin:serviceDesigner.help.release")}</p></>}
      tabs={[
        { key: "overview", label: t("admin:serviceDesigner.tabs.overview"), icon: FileText },
        { key: "process", label: t("admin:serviceDesigner.tabs.process"), icon: Workflow },
        { key: "documents", label: t("admin:serviceDesigner.tabs.documents"), icon: ListChecks },
        { key: "access", label: t("admin:serviceDesigner.tabs.access"), icon: KeyRound },
      ]}
      lint={(b) => lint(b, t as T)}
      summary={(b) => t("admin:serviceDesigner.summary", { code: b.code, workflow: b.workflowKey || "—", kind: t(`admin:serviceDesigner.kind.${b.moduleKind}`) })}
      badges={(b) => <Badge variant={b.moduleKind === "Dynamic" ? "gold" : "outline"} size="sm">{t(`admin:serviceDesigner.kind.${b.moduleKind}`)}</Badge>}
      render={(tab, ctx) => tab === "process" ? <ProcessTab ctx={ctx} /> : tab === "documents" ? <DocumentsTab ctx={ctx} /> : tab === "access" ? <AccessTab ctx={ctx} /> : <OverviewTab ctx={ctx} />}
    />
  );
}

const toRef = () => (o: OptionRef): RefOption => ({ key: o.key, name: o.name, nameAr: o.nameAr, hint: o.departmentKey ?? (o.published === false ? "draft" : null) });

function OverviewTab({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  const { body, patch, readOnly } = ctx;
  const compiled = body.moduleKind === "Compiled";
  const text = (k: keyof ServiceBody, label: string, opts: { rtl?: boolean; area?: boolean; testId?: string } = {}) => (
    <Field label={label}>
      {opts.area
        ? <Textarea data-testid={opts.testId} rows={2} dir={opts.rtl ? "rtl" : undefined} value={(body[k] as string | null) ?? ""} disabled={readOnly} onChange={(e) => patch({ [k]: e.target.value || null } as Partial<ServiceBody>)} />
        : <Input data-testid={opts.testId} dir={opts.rtl ? "rtl" : undefined} value={(body[k] as string | null) ?? ""} disabled={readOnly} onChange={(e) => patch({ [k]: e.target.value || null } as Partial<ServiceBody>)} />}
    </Field>
  );
  return (
    <div className="space-y-4">
      {compiled ? <InlineAlert tone="info" title={t("admin:serviceDesigner.compiledTitle")}>{t("admin:serviceDesigner.compiledBody", { type: body.legacyCaseType ?? "—" })}</InlineAlert> : null}
      <Card>
        <CardHeader><CardTitle>{t("admin:serviceDesigner.identity.title")}</CardTitle><CardDescription>{t("admin:serviceDesigner.identity.intro")}</CardDescription></CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2">
          <Field label={t("admin:common.name")} required><Input data-testid="service-name" value={body.name} disabled={readOnly} onChange={(e) => patch({ name: e.target.value })} /></Field>
          <Field label={t("admin:common.nameAr")}><Input dir="rtl" value={body.nameAr ?? ""} disabled={readOnly} onChange={(e) => patch({ nameAr: e.target.value || null })} /></Field>
          <Field label={t("admin:serviceDesigner.fields.category")}><Input data-testid="service-category" value={body.category} disabled={readOnly} onChange={(e) => patch({ category: e.target.value })} /></Field>
          <Field label={t("admin:serviceDesigner.fields.categoryAr")}><Input dir="rtl" value={body.categoryAr ?? ""} disabled={readOnly} onChange={(e) => patch({ categoryAr: e.target.value || null })} /></Field>
          <Field label={t("admin:serviceDesigner.fields.audience")}>
            <NativeSelect data-testid="service-audience" value={body.audience} disabled={readOnly} onChange={(e) => patch({ audience: e.target.value as ServiceBody["audience"] })}>
              {(["External", "Internal", "Any"] as const).map((a) => <option key={a} value={a}>{t(`admin:serviceDesigner.audience.${a}`)}</option>)}
            </NativeSelect>
          </Field>
          <Field label={t("admin:serviceDesigner.fields.icon")}>
            <NativeSelect value={body.icon} disabled={readOnly} onChange={(e) => patch({ icon: e.target.value })}>
              {[...new Set([body.icon, ...SERVICE_ICONS])].map((i) => <option key={i} value={i}>{i}</option>)}
            </NativeSelect>
          </Field>
          <label className="flex items-center gap-2 text-sm"><Switch data-testid="service-portal" checked={body.portalVisible} disabled={readOnly} onCheckedChange={(c) => patch({ portalVisible: c })} /> {t("admin:serviceDesigner.fields.portalVisible")}</label>
          <Field label={t("admin:serviceDesigner.fields.sortOrder")}><Input type="number" value={body.sortOrder} disabled={readOnly} onChange={(e) => patch({ sortOrder: Number(e.target.value) || 0 })} /></Field>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>{t("admin:serviceDesigner.card.title")}</CardTitle><CardDescription>{t("admin:serviceDesigner.card.intro")}</CardDescription></CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2">
          {text("summary", t("admin:serviceDesigner.fields.summary"), { area: true, testId: "service-summary" })}
          {text("summaryAr", t("admin:serviceDesigner.fields.summaryAr"), { area: true, rtl: true })}
          {text("legalBasis", t("admin:serviceDesigner.fields.legalBasis"), { testId: "service-legal" })}
          {text("legalBasisAr", t("admin:serviceDesigner.fields.legalBasisAr"), { rtl: true })}
          {text("processingTime", t("admin:serviceDesigner.fields.processingTime"), { testId: "service-time" })}
          {text("processingTimeAr", t("admin:serviceDesigner.fields.processingTimeAr"), { rtl: true })}
        </CardContent>
      </Card>
    </div>
  );
}

function ProcessTab({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { body, patch, readOnly } = ctx;
  const options = useServiceOptions();
  const preview = useNumberPreview(body.caseNumberPattern, body.code);
  const o = options.data;
  const ref = toRef();
  const label = (x: OptionRef) => (locale === "ar" && x.nameAr ? x.nameAr : x.name);
  const workflow = o?.workflows.find((w) => w.key === body.workflowKey);
  const form = o?.forms.find((f) => f.key === body.intakeFormKey);
  if (options.isLoading) return <Skeleton className="h-96" />;
  return (
    <div className="grid gap-4 xl:grid-cols-2">
      <Card>
        <CardHeader><CardTitle>{t("admin:serviceDesigner.process.title")}</CardTitle><CardDescription>{t("admin:serviceDesigner.process.intro")}</CardDescription></CardHeader>
        <CardContent className="space-y-3">
          <Field label={t("admin:serviceDesigner.fields.workflow")} hint={workflow ? (workflow.live ? t("admin:serviceDesigner.process.live") : t("admin:serviceDesigner.process.notLive")) : t("admin:serviceDesigner.process.pickWorkflow")}>
            <div className="flex gap-2">
              <NativeSelect data-testid="service-workflow" value={body.workflowKey} disabled={readOnly} onChange={(e) => patch({ workflowKey: e.target.value })}>
                <option value="">—</option>
                {o?.workflows.map((w) => <option key={w.key} value={w.key}>{label(w)}{w.live ? "" : ` (${t("admin:serviceDesigner.process.draft")})`}</option>)}
              </NativeSelect>
              {body.workflowKey ? <Button asChild size="sm" variant="outline"><Link to={`/workflows/${encodeURIComponent(body.workflowKey)}`}><ExternalLink /> {t("admin:serviceDesigner.process.openWorkflow")}</Link></Button> : null}
            </div>
          </Field>
          <Field label={t("admin:serviceDesigner.fields.form")} hint={form && form.published === false ? t("admin:serviceDesigner.process.formDraft") : t("admin:serviceDesigner.process.formHint")}>
            <div className="flex gap-2">
              <NativeSelect data-testid="service-form" value={body.intakeFormKey ?? ""} disabled={readOnly} onChange={(e) => patch({ intakeFormKey: e.target.value || null })}>
                <option value="">{t("admin:serviceDesigner.process.noForm")}</option>
                {o?.forms.map((f) => <option key={f.key} value={f.key}>{label(f)}</option>)}
              </NativeSelect>
              {body.intakeFormKey ? <Button asChild size="sm" variant="outline"><Link to={`/forms/${encodeURIComponent(body.intakeFormKey)}`}><ExternalLink /> {t("admin:serviceDesigner.process.openForm")}</Link></Button> : null}
            </div>
          </Field>
          <Field label={t("admin:serviceDesigner.fields.rules")} hint={t("admin:serviceDesigner.process.rulesHint")}>
            <RefPicker options={(o?.rules ?? []).map(ref)} value={body.eligibilityRuleKeys} readOnly={readOnly} onChange={(v) => patch({ eligibilityRuleKeys: v })} emptyLabel={t("admin:serviceDesigner.process.none")} maxHeight="max-h-40" />
          </Field>
          <Field label={t("admin:serviceDesigner.fields.automations")}>
            <RefPicker options={(o?.automations ?? []).map(ref)} value={body.automationKeys} readOnly={readOnly} onChange={(v) => patch({ automationKeys: v })} emptyLabel={t("admin:serviceDesigner.process.none")} maxHeight="max-h-40" />
          </Field>
          <Field label={t("admin:serviceDesigner.fields.agents")}>
            <RefPicker options={(o?.agents ?? []).map(ref)} value={body.agentKeys} readOnly={readOnly} onChange={(v) => patch({ agentKeys: v })} emptyLabel={t("admin:serviceDesigner.process.none")} maxHeight="max-h-40" />
          </Field>
        </CardContent>
      </Card>
      <div className="space-y-4">
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Hash className="size-4" /> {t("admin:serviceDesigner.numbers.title")}</CardTitle><CardDescription>{t("admin:serviceDesigner.numbers.intro")}</CardDescription></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t("admin:serviceDesigner.fields.code")} hint={t("admin:serviceDesigner.fields.codeHint")}><Input data-testid="service-code" value={body.code} maxLength={6} className="font-mono" disabled={readOnly} onChange={(e) => patch({ code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") })} /></Field>
              <Field label={t("admin:serviceDesigner.fields.pattern")} hint={t("admin:serviceDesigner.numbers.tokens")}><Input data-testid="service-pattern" value={body.caseNumberPattern} dir="ltr" className="font-mono" disabled={readOnly} onChange={(e) => patch({ caseNumberPattern: e.target.value })} /></Field>
            </div>
            {preview.data?.issues.length ? <InlineAlert tone="danger">{preview.data.issues.join(" ")}</InlineAlert> : (
              <div data-testid="number-preview" className="flex flex-wrap items-center gap-2 text-sm"><span className="text-muted-foreground">{t("admin:serviceDesigner.numbers.examples")}</span>{preview.data?.examples.map((x) => <Badge key={x} variant="outline" className="font-mono">{x}</Badge>)}</div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>{t("admin:serviceDesigner.fees.title")}</CardTitle><CardDescription>{t("admin:serviceDesigner.fees.intro")}</CardDescription></CardHeader>
          <CardContent className="space-y-3">
            <Field label={t("admin:serviceDesigner.fields.fees")}><RefPicker options={(o?.fees ?? []).map(ref)} value={body.feeKeys} readOnly={readOnly} onChange={(v) => patch({ feeKeys: v })} emptyLabel={t("admin:serviceDesigner.fees.free")} maxHeight="max-h-40" /></Field>
            <Field label={t("admin:serviceDesigner.fields.templates")}><RefPicker options={(o?.templates ?? []).map(ref)} value={body.templateKeys} readOnly={readOnly} onChange={(v) => patch({ templateKeys: v })} emptyLabel={t("admin:serviceDesigner.process.none")} maxHeight="max-h-40" /></Field>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function DocumentsTab({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  const { body, patch, readOnly } = ctx;
  const set = (i: number, p: Partial<RequiredDocument>) => patch((b) => ({ ...b, requiredDocuments: b.requiredDocuments.map((d, j) => (j === i ? { ...d, ...p } : d)) }));
  return (
    <Card>
      <CardHeader><CardTitle>{t("admin:serviceDesigner.documents.title")}</CardTitle><CardDescription>{t("admin:serviceDesigner.documents.intro")}</CardDescription></CardHeader>
      <CardContent className="space-y-2">
        {body.requiredDocuments.length === 0 ? <p className="text-sm text-muted-foreground">{t("admin:serviceDesigner.documents.none")}</p> : null}
        {body.requiredDocuments.map((d, i) => (
          <div key={i} className="grid items-center gap-2 sm:grid-cols-[1.2fr_1.2fr_0.8fr_auto_auto]">
            <Input data-testid={`doc-name-${i}`} value={d.nameEn} placeholder={t("admin:serviceDesigner.documents.name")} disabled={readOnly} onChange={(e) => set(i, { nameEn: e.target.value, key: d.key && d.key !== fieldKeyFromLabel(d.nameEn) ? d.key : fieldKeyFromLabel(e.target.value) })} />
            <Input dir="rtl" value={d.nameAr ?? ""} placeholder={t("admin:serviceDesigner.documents.nameAr")} disabled={readOnly} onChange={(e) => set(i, { nameAr: e.target.value || null })} />
            <Input value={d.key} className="font-mono text-xs" dir="ltr" disabled={readOnly} aria-label={t("admin:common.key")} onChange={(e) => set(i, { key: e.target.value })} />
            <label className="flex items-center gap-2 text-sm"><Switch checked={d.required} disabled={readOnly} onCheckedChange={(c) => set(i, { required: c })} /> {t("admin:serviceDesigner.documents.required")}</label>
            {!readOnly ? <Button size="sm" variant="ghost" aria-label={t("admin:common.remove")} onClick={() => patch((b) => ({ ...b, requiredDocuments: b.requiredDocuments.filter((_, j) => j !== i) }))}><Trash2 /></Button> : <span />}
          </div>
        ))}
        {!readOnly ? <Button data-testid="add-document" size="sm" variant="outline" onClick={() => patch((b) => ({ ...b, requiredDocuments: [...b.requiredDocuments, { key: "", nameEn: "", nameAr: null, required: true }] }))}><Plus /> {t("admin:serviceDesigner.documents.add")}</Button> : null}
      </CardContent>
    </Card>
  );
}

function AccessTab({ ctx }: { ctx: Ctx }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { body, patch, readOnly } = ctx;
  const options = useServiceOptions();
  const o = options.data;
  const department = body.settings.workspaceKey ?? "";
  const roles = (o?.roles ?? []).filter((r) => !department || !r.departmentKey || r.departmentKey === department).map(toRef());
  if (options.isLoading) return <Skeleton className="h-96" />;
  return (
    <div className="grid gap-4 xl:grid-cols-2">
      <Card>
        <CardHeader><CardTitle>{t("admin:serviceDesigner.access.department")}</CardTitle><CardDescription>{t("admin:serviceDesigner.access.departmentIntro")}</CardDescription></CardHeader>
        <CardContent className="space-y-3">
          <Field label={t("admin:serviceDesigner.fields.department")}>
            <NativeSelect data-testid="service-department" value={department} disabled={readOnly} onChange={(e) => patch((b) => ({ ...b, settings: { ...b.settings, workspaceKey: e.target.value || null } }))}>
              <option value="">{t("admin:serviceDesigner.access.shared")}</option>
              {o?.workspaces.map((w) => <option key={w.key} value={w.key}>{locale === "ar" && w.nameAr ? w.nameAr : w.name}</option>)}
            </NativeSelect>
          </Field>
          <Field label={t("admin:serviceDesigner.fields.flag")} hint={t("admin:serviceDesigner.access.flagHint")}>
            <Input value={body.intakeFlagKey ?? ""} dir="ltr" className="font-mono" disabled={readOnly} placeholder="portal.dynamicIntake.xx" onChange={(e) => patch({ intakeFlagKey: e.target.value || null })} />
          </Field>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>{t("admin:serviceDesigner.access.roles")}</CardTitle><CardDescription>{t("admin:serviceDesigner.access.rolesIntro")}</CardDescription></CardHeader>
        <CardContent className="space-y-3">
          <Field label={t("admin:serviceDesigner.fields.accessRoles")} hint={t("admin:serviceDesigner.access.accessHint")}>
            <RefPicker options={roles} value={body.accessRoleKeys} readOnly={readOnly} onChange={(v) => patch({ accessRoleKeys: v })} emptyLabel={t("admin:serviceDesigner.access.everyone")} maxHeight="max-h-52" />
          </Field>
          <Field label={t("admin:serviceDesigner.fields.startRoles")} hint={t("admin:serviceDesigner.access.startHint")}>
            <RefPicker options={roles} value={body.startRoleKeys} readOnly={readOnly} onChange={(v) => patch({ startRoleKeys: v })} emptyLabel={t("admin:serviceDesigner.access.everyone")} maxHeight="max-h-52" />
          </Field>
        </CardContent>
      </Card>
    </div>
  );
}
